// Chart.js conventions for the District Insights dashboard.
//
// Five canvases across four tabs (the Outliers scatter, the trajectory lines, the
// engagement mix strip, the dose-response bars, the fleet bars) share one frame.
// Spelled out per canvas, the copies disagree and nobody can tell which
// differences are deliberate. These helpers hold the parts that must not vary and
// leave the rest to the caller.
//
// Two helpers rather than one, because the five canvases agree about the frame but
// not about the axes. `chartOptions` is the frame: it is correct for every canvas
// here, including the scatter. `categoryScales` is the axis convention for the
// four charts whose x is a set of labelled bins -- months, weeks, dosage tiers --
// and whose y is a count or rate rising from zero. The scatter's axes are two
// continuous measures with their own bounds and a visible grid on both, so it
// takes the frame and states its own scales. That opt-out is the point of the
// split: one helper would leave it overriding four defaults to get there, and an
// override that exists only to undo a default is how a convention drifts from the
// thing it is supposed to be enforcing.
import { MUTED, GRID } from "./tokens";

// Recursive so a caller can add `scales.y.title` without also having to restate
// `scales.y.grid`, which is the whole reason a shared default is worth having.
// Arrays replace rather than merge (a dataset list is a value, not a shape), and
// an explicit `undefined` deletes -- that is how a caller drops a default it
// genuinely does not want.
const isShape = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v) && !(v instanceof Function);

// Deep copy. Functions and primitives pass through; arrays are rebuilt so their
// elements are copied too.
//
// This is load-bearing, not hygiene. Chart.js MUTATES the options object it is
// given -- it normalizes and expands each scale config in place -- so any node a
// returned object still shares with the module-level defaults below gets written
// through by the first chart to render and read back corrupted by the next one.
// A shallow spread per level looks right and is not: `{...CATEGORY_SCALES}` copies
// the `x` reference, and a caller who overrides only `x.stacked` gets an `x.grid`
// that IS the constant's. The symptom is worth recognizing: the fleet bars render
// perfectly on a direct load and come up empty once another tab's chart has
// initialized first, which reads like a data problem and isn't one.
const clone = (v) => {
  if (Array.isArray(v)) return v.map(clone);
  if (!isShape(v)) return v;
  const out = {};
  Object.keys(v).forEach((k) => {
    out[k] = clone(v[k]);
  });
  return out;
};

const merge = (base, over) => {
  if (!isShape(over)) return clone(over);
  const out = clone(base);
  Object.keys(over).forEach((k) => {
    if (over[k] === undefined) delete out[k];
    else if (isShape(base[k]) && isShape(over[k])) out[k] = merge(base[k], over[k]);
    else out[k] = clone(over[k]);
  });
  return out;
};

// The frame every canvas on these pages shares.
//
// `maintainAspectRatio: false` is what makes ChartCard's fixed pixel height the
// height of the chart; without it Chart.js sizes from the width and the card
// clips. `legend: false` because each tab draws its own compact Legend from the
// same swatches its tables use -- Chart.js's would list every gray context school
// besides. `datalabels: false` because that plugin is registered globally, so any
// chart that does not opt out gets a number stamped on every mark.
const FRAME = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    datalabels: { display: false },
  },
};

const chartOptions = (over) => merge(FRAME, over || {});

// x is a set of labelled bins, y a count or rate from zero. No x gridlines: the
// bins are the categories, so vertical rules divide nothing. beginAtZero because
// every y here is a magnitude, and a chart of counts that starts at 40 overstates
// every difference on it.
const CATEGORY_SCALES = {
  x: { grid: { display: false }, ticks: { color: MUTED } },
  y: { beginAtZero: true, grid: { color: GRID } },
};

const categoryScales = (over) => merge(CATEGORY_SCALES, over || {});

// An axis title, in the one form these charts use it.
const axisTitle = (text) => ({ display: true, text, color: MUTED });

// Cap on a single bar's width. Without it a window holding one or two buckets
// draws bars as wide as the panel, which reads as a broken chart rather than as a
// short window. Shared so the Overview's fleet spark can size its columns to
// match the full Fleet chart it previews.
const MAX_BAR_THICKNESS = 42;

export { chartOptions, categoryScales, axisTitle, MAX_BAR_THICKNESS };
